import type { Profile } from '@/types';

/**
 * `profiles.links` is a Json column the OG treats as a `{ platform: url }` map
 * (e.g. `{ x: '#', linkedin: '#' }`, data.js USERS). Parse defensively.
 */
export function parseLinks(links: Profile['links']): Record<string, string> {
  if (!links || typeof links !== 'object' || Array.isArray(links)) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(links as Record<string, unknown>)) {
    if (typeof v === 'string' && v) out[k] = v;
  }
  return out;
}

/** `profiles.skills` / `interests` are Json arrays of strings. */
export function parseStringArray(value: Profile['skills']): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string' && v.trim().length > 0);
}

/** "@maya" → "maya" for display; leaves a bare handle untouched. */
export function stripHandle(handle?: string | null): string {
  return (handle ?? '').replace(/^@/, '');
}

/** created_at → "Apr 2024" (OG join-date format, supabase.js:78). */
export function joinDate(createdAt?: string | null): string {
  if (!createdAt) return '';
  return new Date(createdAt).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

/**
 * Whole days a member has been a WINR (created_at → today). Clamped at >= 0 so a
 * clock-skewed future timestamp can't read negative; returns null when there's
 * no join date to count from. Day 0 (joined today) reads as 0.
 */
export function winrDays(createdAt?: string | null, now: Date = new Date()): number | null {
  if (!createdAt) return null;
  const joined = new Date(createdAt);
  if (Number.isNaN(joined.getTime())) return null;
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.max(0, Math.floor((now.getTime() - joined.getTime()) / msPerDay));
}
